import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { toast } from 'react-toastify';
import { useLocation, useNavigate } from 'react-router-dom';
import { CARD_STYLES } from '../../../assets/constants/cardStyles';
import { CARD_TYPES } from '../../../assets/constants/cardTypes';
import { CARD_ISSUANCE_MAINTENANCE } from '../../../assets/constants/cardIssuance';
import { MIN_DEPOSIT_TRANSACTION_USD } from '../../../assets/constants/depositData';
import { getCardStyleName } from '../../../assets/constants/cardSensitive';
import { CurrentUserContext } from '../../../assets/contexts/currentUserContext';
import { TranslationContext } from '../../../assets/contexts/translationContext';
import { CopyIcon, RightArrowIcon } from '../../../assets/icons/icons';
import useFundingQuote from '../../../assets/hooks/useFundingQuote';
import {
  calculateMinCardIssueTopUpAmount,
  createRemoteCard,
  getCard,
  getCardById,
  getCards,
  getRemoteCardDepositAddress,
  isCardLimitReached,
  refreshCardById,
} from '../../../assets/services/cards';
import {
  buildQuickTopUpAmounts,
  calculateRequiredUsdTopUpForCardBalance,
  formatFundingQuoteCommissionPercent,
  getDefaultFundingAsset,
  loadFundingAssets,
} from '../../../assets/services/fundingAssets';
import {
  cancelDepositFlow,
  startDepositFlow,
  subscribeDepositSession,
} from '../../../assets/services/deposit';
import {
  decimalEq,
  decimalGt,
  decimalGte,
  decimalPercentString,
  decimalToString,
  divideDecimalStrings,
  formatDecimal,
  parseDecimalInput,
  sanitizeDecimalInput,
} from '../../../assets/utils/decimalValues';
import { getCurrencyIconUrl } from '../../../assets/utils/currencyIcon';
import { triggerImpactHaptic } from '../../../assets/utils/hapticFeedback';
import BankCard from '../../BankCard/BankCard';
import CardPage from '../CardPage/CardPage';
import MiniPreloader from '../../MiniPreloader/MiniPreloader';
import ScreenHeader from '../../ScreenHeader/ScreenHeader';
import BottomActionButton from '../../shared/BottomActionButton/BottomActionButton';
import CurrencyIcon from '../../shared/CurrencyIcon/CurrencyIcon';
import CurrencyQrCode from '../../shared/CurrencyQrCode/CurrencyQrCode';
import FundingAssetSheet from '../../shared/FundingAssetSheet/FundingAssetSheet';
import DepositOperations from '../../HomePages/DepositPages/DepositOperations/DepositOperations';
import './NewCard.css';

const STEP = {
  SETUP: 'setup',
  AMOUNT: 'amount',
  ADDRESS: 'address',
};

const SCREEN_TRANSITION = {
  duration: 0.28,
  ease: [0.22, 1, 0.36, 1],
};

const CYRILLIC_TO_LATIN = {
  а: 'A',
  б: 'B',
  в: 'V',
  г: 'G',
  д: 'D',
  е: 'E',
  ё: 'E',
  ж: 'ZH',
  з: 'Z',
  и: 'I',
  й: 'I',
  к: 'K',
  л: 'L',
  м: 'M',
  н: 'N',
  о: 'O',
  п: 'P',
  р: 'R',
  с: 'S',
  т: 'T',
  у: 'U',
  ф: 'F',
  х: 'KH',
  ц: 'TS',
  ч: 'CH',
  ш: 'SH',
  щ: 'SHCH',
  ъ: '',
  ы: 'Y',
  ь: '',
  э: 'E',
  ю: 'YU',
  я: 'YA',
};

function normalizeNameOnCardInput(value) {
  return Array.from(String(value || ''))
    .map((char) => {
      const lowerChar = char.toLowerCase();
      return CYRILLIC_TO_LATIN[lowerChar] ?? char;
    })
    .join('')
    .toUpperCase()
    .replace(/[^A-Z ]/g, ' ')
    .replace(/\s+/g, ' ')
    .slice(0, 26);
}

function normalizeCardNameInput(value) {
  return String(value || '')
    .replace(/\s+/g, ' ')
    .replace(/^\s+/, '')
    .slice(0, 32);
}

function getCurrentUserCardName(currentUser) {
  const personalData = currentUser?.personal_data || {};
  const candidates = [
    personalData.name,
    personalData.full_name,
    personalData.fullName,
    [personalData.first_name, personalData.last_name].filter(Boolean).join(' '),
    currentUser?.name,
    currentUser?.full_name,
    currentUser?.fullName,
    [currentUser?.first_name, currentUser?.last_name].filter(Boolean).join(' '),
  ];

  return candidates.find((value) => typeof value === 'string' && value.trim()) || '';
}

function getDefaultNameOnCard(currentUser) {
  return normalizeNameOnCardInput(getCurrentUserCardName(currentUser)).trim();
}

function normalizeAmountInput(value, decimals = 2) {
  const sanitized = sanitizeDecimalInput(value, decimals);
  const [integerPart = '', fractionPart] = sanitized.split('.');
  const cappedIntegerPart = integerPart.slice(0, 8);

  return fractionPart == null ? cappedIntegerPart : `${cappedIntegerPart}.${fractionPart}`;
}

function formatExpectedAmount(amount, asset, decimals = 2) {
  const decimal = parseDecimalInput(amount);
  const currency = asset ? ` ${asset}` : '';

  if (!decimal || decimal.lte(0)) {
    return `0${currency}`;
  }

  return `${formatDecimal(decimal, decimals)}${currency}`;
}

function formatTopUpAmount(value, asset, decimals = 2) {
  const decimal = parseDecimalInput(value);
  const currency = asset ? ` ${asset}` : '';

  if (!decimal || decimal.lte(0)) {
    return `0.00${currency}`;
  }

  return `${formatDecimal(decimal, decimals, {
    minFractionDigits: Math.min(decimals, 2),
  })}${currency}`;
}

function formatProgressAssetValue(value, decimals = 2) {
  const decimal = parseDecimalInput(value);

  if (!decimal || decimal.lte(0)) {
    return '0';
  }

  return formatDecimal(decimal, decimals);
}

function formatCardBalanceAmount(value) {
  const decimal = parseDecimalInput(value);

  if (!decimal || decimal.lte(0)) {
    return '$0.00';
  }

  return formatDecimal(decimal, 2, {
    prefix: '$',
    minFractionDigits: 2,
  });
}

export function getDisplayedCardBalance(balanceValue, cardPriceValue) {
  const balance = parseDecimalInput(balanceValue);
  const cardPrice = parseDecimalInput(cardPriceValue);

  if (!balance || balance.lte(0)) {
    return '0';
  }

  const displayedBalance = cardPrice ? balance.minus(cardPrice) : balance;

  return displayedBalance.gt(0) ? displayedBalance.toString() : '0';
}

function getMinimumFundingAmount(minimumUsd, fundingAsset) {
  const rateToUsd = fundingAsset?.rateToUsd || '1';
  const decimalPlaces = fundingAsset?.decimalPlaces ?? 2;
  const fundingAmount = decimalGt(rateToUsd, '0')
    ? divideDecimalStrings(minimumUsd, rateToUsd)
    : minimumUsd;
  const [minimumAmount] = buildQuickTopUpAmounts(fundingAmount, decimalPlaces);

  return minimumAmount || formatDecimal(fundingAmount, decimalPlaces);
}

async function copyToClipboard(value) {
  const text = String(value ?? '');

  if (!text) {
    return false;
  }

  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Fall back to the legacy path below.
    }
  }

  const textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.setAttribute('readonly', '');
  textArea.style.position = 'fixed';
  textArea.style.opacity = '0';
  textArea.style.pointerEvents = 'none';
  document.body.appendChild(textArea);
  textArea.select();

  try {
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    document.body.removeChild(textArea);
  }
}

function DepositQrCode({ iconUrl, label, value }) {
  return (
    <div className="new-card__qr" aria-label={label}>
      <CurrencyQrCode value={value} iconUrl={iconUrl} />
    </div>
  );
}

function NewCard({ showBack = true }) {
  const navigate = useNavigate();
  const location = useLocation();
  const shouldReduceMotion = useReducedMotion();
  const { card, common, deposit, home, t } = useContext(TranslationContext);
  const { currentUser } = useContext(CurrentUserContext);
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const isContinueIssue = searchParams.get('continueIssue') === '1';
  const continueIssueCardId = searchParams.get('cardId')
    || location.state?.cardId
    || '';
  const storedCard = isContinueIssue
    ? getCardById(continueIssueCardId)
    : getCard();
  const hasEditedNameOnCardRef = useRef(false);
  const autoFilledAmountRef = useRef('');
  const pendingFundingAssetRef = useRef(null);
  const [step, setStep] = useState(isContinueIssue ? STEP.AMOUNT : STEP.SETUP);
  const [style, setStyle] = useState('sunset');
  const [cardType, setCardType] = useState(CARD_TYPES.VIRTUAL);
  const [cardName, setCardName] = useState('');
  const [nameOnCard, setNameOnCard] = useState('');
  const [createdCard, setCreatedCard] = useState(storedCard);
  const [fundingAssets, setFundingAssets] = useState([]);
  const [isFundingAssetsLoading, setIsFundingAssetsLoading] = useState(false);
  const [fundingAssetsError, setFundingAssetsError] = useState('');
  const [selectedFundingAsset, setSelectedFundingAsset] = useState(null);
  const [depositAddress, setDepositAddress] = useState(storedCard?.depositAddress || null);
  const [amount, setAmount] = useState('');
  const [amountError, setAmountError] = useState('');
  const [isSubmittingSetup, setIsSubmittingSetup] = useState(false);
  const [isRequestingAddress, setIsRequestingAddress] = useState(false);
  const [isAssetSheetOpen, setIsAssetSheetOpen] = useState(false);
  const [receivedTopUpAssetAmount, setReceivedTopUpAssetAmount] = useState('0');
  const [depositOperations, setDepositOperations] = useState([]);
  const cleanCardName = cardName.trim().replace(/\s+/g, ' ');
  const cleanName = nameOnCard.trim().replace(/\s+/g, ' ');
  const selectedAssetCode = selectedFundingAsset?.asset || '';
  const selectedCurrencyIconUrl = getCurrencyIconUrl(selectedFundingAsset);
  const selectedNetworkCode = selectedFundingAsset?.network || '';
  const selectedAssetDecimals = selectedFundingAsset?.decimalPlaces ?? 2;
  const selectedTopUpAddress = depositAddress?.address || '';
  const selectedMinimumAmount = decimalToString(calculateMinCardIssueTopUpAmount(createdCard));
  const fallbackRequiredTopUpUsdAmount = calculateRequiredUsdTopUpForCardBalance(
    selectedMinimumAmount,
    selectedFundingAsset,
  );
  const fallbackMinimumFundingAmount = getMinimumFundingAmount(
    fallbackRequiredTopUpUsdAmount,
    selectedFundingAsset,
  );
  const {
    quote: fundingQuote,
    quoteError: fundingQuoteError,
    isQuoteLoading: isFundingQuoteLoading,
    minimumQuote,
    minimumQuoteError,
    isMinimumQuoteLoading,
  } = useFundingQuote({
    amount,
    fundingAsset: selectedFundingAsset,
    minimumBalanceAmount: selectedMinimumAmount,
  });
  const shouldReuseMinimumTransactionQuote = decimalEq(
    selectedMinimumAmount,
    MIN_DEPOSIT_TRANSACTION_USD,
  );
  const {
    minimumQuote: separatelyCalculatedMinimumTransactionQuote,
    isMinimumQuoteLoading: isSeparateMinimumTransactionQuoteLoading,
  } = useFundingQuote({
    amount: '',
    fundingAsset: selectedFundingAsset,
    minimumBalanceAmount: MIN_DEPOSIT_TRANSACTION_USD,
    enabled: !shouldReuseMinimumTransactionQuote,
  });
  const minimumTransactionQuote = shouldReuseMinimumTransactionQuote
    ? minimumQuote
    : separatelyCalculatedMinimumTransactionQuote;
  const isMinimumTransactionQuoteLoading = shouldReuseMinimumTransactionQuote
    ? isMinimumQuoteLoading
    : isSeparateMinimumTransactionQuoteLoading;
  const confirmedMinimumFundingAmount = minimumQuote?.fundingAmount || '';
  const minimumFundingAmount = confirmedMinimumFundingAmount || fallbackMinimumFundingAmount;
  const minimumTransactionFundingAmount = minimumTransactionQuote?.fundingAmount || '';
  const minimumTransactionFundingAmountValue = formatProgressAssetValue(
    minimumTransactionFundingAmount,
    selectedAssetDecimals,
  );
  const minimumTransferWarning = minimumTransactionFundingAmount && selectedAssetCode
    ? t('deposit.doNotSendLessAmount', {
      amount: minimumTransactionFundingAmountValue,
      asset: selectedAssetCode,
    })
    : deposit.doNotSendLess;
  const quickTopUpAmounts = useMemo(
    () => buildQuickTopUpAmounts(minimumFundingAmount, selectedAssetDecimals),
    [minimumFundingAmount, selectedAssetDecimals],
  );
  const defaultTopUpAmount = quickTopUpAmounts[0] || '';
  const expectedAmount = useMemo(
    () => formatExpectedAmount(amount, selectedAssetCode, selectedAssetDecimals),
    [amount, selectedAssetCode, selectedAssetDecimals],
  );
  const cardBalanceTopUp = fundingQuote?.balanceAmount || '0';
  const cardIssuePrice = createdCard?.cardPrice || '0';
  const displayedCardBalance = getDisplayedCardBalance(cardBalanceTopUp, cardIssuePrice);
  const topUpFeePercent = fundingQuote
    ? formatFundingQuoteCommissionPercent(fundingQuote)
    : '—';
  const expectedTopUpAmount = cardBalanceTopUp;
  const isQuoteLoading = isFundingQuoteLoading || (!amount && isMinimumQuoteLoading);
  const calculationError = fundingQuoteError || (!amount && minimumQuoteError);
  const topUpProgressPercent = decimalPercentString(receivedTopUpAssetAmount, amount);
  const receivedTopUpLabel = formatProgressAssetValue(
    receivedTopUpAssetAmount,
    selectedAssetDecimals,
  );
  const expectedTopUpLabel = formatProgressAssetValue(amount, selectedAssetDecimals);
  const assetNetworkLabel = selectedAssetCode && selectedNetworkCode
    ? `${selectedAssetCode} (${selectedNetworkCode})`
    : selectedAssetCode || selectedNetworkCode;
  const previewName = cleanCardName;
  const cardTypes = useMemo(() => [
    {
      id: CARD_TYPES.VIRTUAL,
      label: card.virtual,
      description: card.virtualDescription,
    },
    {
      id: CARD_TYPES.PHYSICAL,
      label: card.physical,
      description: card.physicalDescription,
      disabled: true,
    },
  ], [card]);
  const defaultNameOnCard = useMemo(() => getDefaultNameOnCard(currentUser), [currentUser]);
  const handleAssetSheetOpen = useCallback(() => {
    pendingFundingAssetRef.current = null;
    setIsAssetSheetOpen(true);
  }, []);
  const handleAssetSheetClose = useCallback(() => {
    setIsAssetSheetOpen(false);
  }, []);
  const handleFundingAssetSelect = useCallback((asset) => {
    pendingFundingAssetRef.current = asset;
  }, []);
  const handleAssetSheetExitComplete = useCallback(() => {
    const asset = pendingFundingAssetRef.current;

    if (!asset) return;

    pendingFundingAssetRef.current = null;
    setSelectedFundingAsset(asset);
    setAmountError('');
  }, []);

  useEffect(() => {
    if (isContinueIssue || hasEditedNameOnCardRef.current || nameOnCard.trim() || !defaultNameOnCard) {
      return;
    }

    setNameOnCard(defaultNameOnCard);
  }, [defaultNameOnCard, isContinueIssue, nameOnCard]);

  useEffect(() => {
    if (!isContinueIssue || !continueIssueCardId || createdCard?.id === continueIssueCardId) {
      return undefined;
    }

    const cachedCard = getCardById(continueIssueCardId);

    if (cachedCard) {
      setCreatedCard(cachedCard);
      setDepositAddress(cachedCard.depositAddress || null);
      return undefined;
    }

    let isMounted = true;

    refreshCardById(continueIssueCardId)
      .then((nextCard) => {
        if (!isMounted) return;
        setCreatedCard(nextCard);
        setDepositAddress(nextCard?.depositAddress || null);
      })
      .catch(() => {
        if (isMounted) {
          toast.error(common.failed);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [common.failed, continueIssueCardId, createdCard?.id, isContinueIssue]);

  useEffect(() => {
    let isMounted = true;

    setIsFundingAssetsLoading(true);
    setFundingAssetsError('');

    loadFundingAssets()
      .then((assets) => {
        if (!isMounted) return;

        setFundingAssets(assets);
        setSelectedFundingAsset((currentAsset) => {
          const storedIssueTopUp = storedCard?.issueTopUp;
          const storedAsset = assets.find((assetOption) => (
            assetOption.currencyId === storedIssueTopUp?.currencyId
            && (assetOption.networkId === storedIssueTopUp?.networkId
              || assetOption.network === storedIssueTopUp?.network)
          ));

          return currentAsset || storedAsset || getDefaultFundingAsset(assets);
        });
      })
      .catch(() => {
        if (!isMounted) return;
        setFundingAssetsError(common.failed || 'Failed');
      })
      .finally(() => {
        if (isMounted) {
          setIsFundingAssetsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [common.failed, storedCard]);

  useEffect(() => {
    if (
      step !== STEP.AMOUNT
      || !createdCard?.id
      || !selectedFundingAsset
      || !defaultTopUpAmount
      || !decimalGt(defaultTopUpAmount, '0')
    ) {
      return;
    }

    setAmount((currentAmount) => {
      if (currentAmount && currentAmount !== autoFilledAmountRef.current) {
        return currentAmount;
      }

      autoFilledAmountRef.current = defaultTopUpAmount;
      return defaultTopUpAmount;
    });
  }, [createdCard?.id, defaultTopUpAmount, selectedFundingAsset, step]);

  useEffect(() => {
    if (step !== STEP.ADDRESS || !createdCard?.id || !depositAddress?._id) {
      return undefined;
    }

    let didOpenStatusFlow = false;

    const unsubscribe = subscribeDepositSession((nextSession) => {
      if (!nextSession) return;

      setReceivedTopUpAssetAmount(decimalToString(nextSession.receivedAssetAmount ?? '0'));
      setDepositOperations(nextSession.operations || []);

      if (nextSession.shouldOpenStatusFlow && nextSession.status === 'confirming') {
        didOpenStatusFlow = true;
        navigate('/cards/issue/confirming', {
          replace: true,
          state: { cardId: createdCard.id },
        });
      }

      if (nextSession.status === 'success') {
        didOpenStatusFlow = true;
        refreshCardById(createdCard.id).catch(() => {});
        navigate('/cards/issue/submitted', {
          replace: true,
          state: { cardId: createdCard.id },
        });
      }
    });

    setReceivedTopUpAssetAmount('0');
    setDepositOperations([]);
    startDepositFlow({
      cardId: createdCard.id,
      amount,
      asset: selectedFundingAsset?.asset,
      network: selectedFundingAsset?.network,
      address: depositAddress.address,
      addressId: depositAddress._id,
      memo: depositAddress.memo,
      minimumAmount: selectedMinimumAmount,
      balanceAmountUsd: cardBalanceTopUp,
      expectedAmountUsd: expectedTopUpAmount,
    });

    return () => {
      unsubscribe();
      if (!didOpenStatusFlow) {
        cancelDepositFlow();
      }
    };
  }, [
    amount,
    cardBalanceTopUp,
    createdCard,
    depositAddress,
    expectedTopUpAmount,
    navigate,
    selectedFundingAsset,
    selectedMinimumAmount,
    step,
  ]);

  const handleBack = () => {
    if (step === STEP.ADDRESS) {
      setStep(STEP.AMOUNT);
      return;
    }

    if (step === STEP.AMOUNT) {
      navigate('/home', { replace: !showBack });
      return;
    }

    if (showBack) {
      navigate('/home');
    }
  };

  const handleSetupSubmit = async () => {
    if (!isContinueIssue && isCardLimitReached(getCards(), currentUser)) {
      toast.error(home.cardLimitReached);
      navigate('/home', { replace: true });
      return;
    }

    if (!cleanName) {
      return;
    }

    setIsSubmittingSetup(true);

    try {
      const nextCard = await createRemoteCard({
        type: cardType,
        style,
        name: cleanCardName,
        nameOnCard: cleanName,
      });

      setCreatedCard(nextCard);
      const nextMinimumAmount = calculateMinCardIssueTopUpAmount(nextCard);
      const nextRequiredTopUpUsdAmount = calculateRequiredUsdTopUpForCardBalance(
        nextMinimumAmount,
        selectedFundingAsset,
      );
      const nextMinimumFundingAmount = getMinimumFundingAmount(
        nextRequiredTopUpUsdAmount,
        selectedFundingAsset,
      );

      autoFilledAmountRef.current = nextMinimumFundingAmount;
      setAmount(nextMinimumFundingAmount);
      setStep(STEP.AMOUNT);
    } catch (error) {
      toast.error(error?.message || common.failed);
    } finally {
      setIsSubmittingSetup(false);
    }
  };

  const handleAmountSubmit = async () => {
    if (CARD_ISSUANCE_MAINTENANCE) {
      return;
    }

    if (!selectedFundingAsset) {
      setAmountError(deposit.selectFirst);
      return;
    }

    if (!createdCard?.id) {
      toast.error(common.failed);
      return;
    }

    if (
      isFundingQuoteLoading
      || isMinimumQuoteLoading
      || isMinimumTransactionQuoteLoading
      || !fundingQuote
      || !minimumQuote
      || !minimumTransactionQuote
    ) {
      setAmountError(common.failed);
      return;
    }

    if (!decimalGte(cardBalanceTopUp, selectedMinimumAmount)) {
      setAmountError(t('deposit.minimumAmount', {
        amount: formatProgressAssetValue(minimumFundingAmount, selectedAssetDecimals),
        asset: selectedAssetCode,
      }));
      return;
    }

    setAmountError('');
    setIsRequestingAddress(true);

    try {
      const nextAddress = await getRemoteCardDepositAddress(createdCard.id, selectedFundingAsset);
      setDepositAddress(nextAddress);
      setStep(STEP.ADDRESS);
    } catch (error) {
      toast.error(error?.message || common.failed);
    } finally {
      setIsRequestingAddress(false);
    }
  };

  const handleCopyValue = async (value, label) => {
    triggerImpactHaptic('light');
    const isCopied = await copyToClipboard(value);

    if (isCopied) {
      toast(t('common.valueCopied', { label }), {
        autoClose: 1600,
        className: 'app-toast app-toast--copy',
      });
      return;
    }

    toast.error(common.failedToCopy);
  };

  const headerTitle = step === STEP.SETUP
    ? card.newCard
    : step === STEP.AMOUNT
      ? deposit.topUpCard
      : deposit.topUpAddress;
  const headerBack = showBack || step !== STEP.SETUP ? handleBack : undefined;
  const ctaLabel = step === STEP.SETUP || (step === STEP.AMOUNT && CARD_ISSUANCE_MAINTENANCE)
    ? common.continue
    : step === STEP.AMOUNT
      ? deposit.showAddress
      : '';
  const ctaDisabled = step === STEP.SETUP
    ? !cleanName || isSubmittingSetup
    : step === STEP.AMOUNT
      ? CARD_ISSUANCE_MAINTENANCE
        || !createdCard?.id
        || !selectedFundingAsset
        || !amount
        || !fundingQuote
        || !minimumQuote
        || !minimumTransactionQuote
        || isMinimumQuoteLoading
        || isMinimumTransactionQuoteLoading
        || isFundingQuoteLoading
        || isRequestingAddress
        || isFundingAssetsLoading
      : false;
  const handleCta = step === STEP.SETUP
    ? handleSetupSubmit
    : step === STEP.AMOUNT
      ? handleAmountSubmit
      : undefined;
  const shouldShowCta = step !== STEP.ADDRESS;

  const renderSetup = () => (
    <div className="new-card__screen">
      <BankCard
        cardStyle={style}
        cardType={cardType}
        showCardType
        size="detail"
        label={previewName}
        detailsText={card.pendingIssue}
        pending
        className="new-card__preview"
      />

      <label className="new-card__field">
        <span className="card-page__section-label new-card__field-label">
          {card.cardNameSection}
        </span>
        <input
          type="text"
          className="new-card__name-input"
          placeholder={card.cardNamePlaceholder}
          value={cardName}
          autoComplete="off"
          enterKeyHint="next"
          maxLength={32}
          onChange={(event) => setCardName(normalizeCardNameInput(event.target.value))}
        />
      </label>

      <label className="new-card__field">
        <span className="card-page__section-label new-card__field-label">
          {card.nameOnCardSection}
        </span>
        <input
          type="text"
          className="new-card__name-input"
          placeholder={card.namePlaceholder}
          value={nameOnCard}
          autoComplete="name"
          lang="en"
          autoCapitalize="characters"
          enterKeyHint="next"
          maxLength={26}
          onChange={(event) => {
            hasEditedNameOnCardRef.current = true;
            setNameOnCard(normalizeNameOnCardInput(event.target.value));
          }}
        />
      </label>

      <div className="new-card__section">
        <p className="card-page__section-label">{card.styleSection}</p>
        <div className="new-card__style-row" role="listbox" aria-label={card.styleSection}>
          {CARD_STYLES.map((option) => (
            <div
              key={option}
              className={`new-card__style-item${
                style === option ? ' new-card__style-item--selected' : ''
              }`}
            >
              <button
                type="button"
                role="option"
                aria-selected={style === option}
                aria-label={getCardStyleName(option, card.styles)}
                className={`new-card__style-swatch new-card__style-swatch--${option}`}
                onClick={() => setStyle(option)}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="new-card__section">
        <p className="card-page__section-label">{card.typeSection}</p>
        <div className="new-card__types">
          {cardTypes.map((type) => (
            <button
              key={type.id}
              type="button"
              className={
                cardType === type.id
                  ? 'new-card__type-tile new-card__type-tile--selected'
                  : 'new-card__type-tile'
              }
              disabled={type.disabled}
              aria-pressed={cardType === type.id}
              onClick={() => {
                if (!type.disabled) {
                  setCardType(type.id);
                }
              }}
            >
              <span className="new-card__type-title">{type.label}</span>
              <span className="new-card__type-description">{type.description}</span>
              {type.disabled && (
                <span className="new-card__type-soon">{card.soon}</span>
              )}
              <span className="new-card__type-check" aria-hidden="true">
                ✓
              </span>
            </button>
          ))}
        </div>
      </div>

    </div>
  );

  const renderAmount = () => (
    <div className="new-card__screen new-card__screen--amount">
      {CARD_ISSUANCE_MAINTENANCE && (
        <div
          id="card-issuance-maintenance"
          className="new-card__warning new-card__hint-card"
          role="status"
        >
          <strong>{card.issueMaintenanceTitle}</strong>
          <span>{card.issueMaintenanceText}</span>
        </div>
      )}

      <button
        type="button"
        className="new-card__asset-row"
        onClick={handleAssetSheetOpen}
      >
        <CurrencyIcon
          className="new-card__asset-icon"
          imageClassName="new-card__asset-icon-image"
          currency={selectedFundingAsset}
          code={selectedAssetCode}
        />
        <span className="new-card__asset-copy">
          <span className="new-card__asset-label">{deposit.currencyNetwork}</span>
          <span className="new-card__asset-value">
            {selectedFundingAsset
              ? `${selectedFundingAsset.asset} · ${selectedFundingAsset.network}`
              : isFundingAssetsLoading
                ? common.loading
                : deposit.selectCurrencyNetwork}
          </span>
        </span>
        <RightArrowIcon className="new-card__asset-chevron" />
      </button>

      {fundingAssetsError && (
        <p className="new-card__amount-error">{fundingAssetsError}</p>
      )}

      <AnimatePresence initial={false}>
        {selectedFundingAsset && (
          <motion.div
            className="new-card__amount-fields"
            initial={shouldReduceMotion
              ? { opacity: 0, transform: 'translate3d(0, 0px, 0)' }
              : { opacity: 0, transform: 'translate3d(0, 10px, 0)' }}
            animate={{ opacity: 1, transform: 'translate3d(0, 0px, 0)' }}
            exit={shouldReduceMotion
              ? { opacity: 0, transform: 'translate3d(0, 0px, 0)' }
              : { opacity: 0, transform: 'translate3d(0, -6px, 0)' }}
            transition={SCREEN_TRANSITION}
          >
            <label className="new-card__amount-card">
              <span className="new-card__amount-label">{deposit.topUpAmount}</span>
              <span className="new-card__amount-input-row">
                <input
                  type="text"
                  inputMode="decimal"
                  className="new-card__amount-input"
                  placeholder="50"
                  value={amount}
                  onChange={(event) => {
                    autoFilledAmountRef.current = '';
                    setAmount(normalizeAmountInput(event.target.value, selectedAssetDecimals));
                    setAmountError('');
                  }}
                />
                <span className="new-card__amount-currency">{selectedAssetCode}</span>
              </span>
              <span
                className={
                  amountError
                    ? 'new-card__amount-error'
                    : 'new-card__amount-error new-card__amount-error--empty'
                }
              >
                {amountError
                  || (calculationError ? common.failed : '')
                  || (minimumFundingAmount
                    ? t('deposit.minimumAmount', {
                      amount: formatProgressAssetValue(minimumFundingAmount, selectedAssetDecimals),
                      asset: selectedAssetCode,
                    })
                    : '')}
              </span>
              <div className="new-card__quick-amounts" aria-label={deposit.topUpAmount}>
                {quickTopUpAmounts.map((quickAmount) => (
                  <button
                    key={quickAmount}
                    type="button"
                    className={
                      decimalEq(amount, String(quickAmount))
                        ? 'new-card__quick-amount new-card__quick-amount--selected'
                        : 'new-card__quick-amount'
                    }
                    onClick={() => {
                      autoFilledAmountRef.current = '';
                      setAmount(String(quickAmount));
                      setAmountError('');
                    }}
                  >
                    {quickAmount}
                  </button>
                ))}
              </div>
            </label>

            <div className="new-card__top-up-quote">
              <div className="new-card__top-up-quote-main">
                <span className="new-card__top-up-quote-label">{deposit.cardBalanceReceive}</span>
                <div
                  className="new-card__top-up-quote-value"
                  aria-live="polite"
                  aria-busy={isQuoteLoading}
                >
                  {isQuoteLoading
                    ? <MiniPreloader className="new-card__quote-loader" label={common.loading} />
                    : formatCardBalanceAmount(displayedCardBalance)}
                </div>
              </div>

              <div className="new-card__top-up-quote-row">
                <span>{deposit.topUpAmount}</span>
                <strong>{formatTopUpAmount(amount, selectedAssetCode, selectedAssetDecimals)}</strong>
              </div>
              <div className="new-card__top-up-quote-row">
                <span>{card.issueCost}</span>
                <strong>{formatCardBalanceAmount(cardIssuePrice)}</strong>
              </div>
              <div className="new-card__top-up-quote-row">
                <span>{deposit.processingFee}</span>
                {isQuoteLoading
                  ? <MiniPreloader className="new-card__fee-loader" label={common.loading} />
                  : <strong>{topUpFeePercent}</strong>}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  const renderAddress = () => (
    <div className="new-card__screen">
      <div className="new-card__qr-card">
        <DepositQrCode
          label={deposit.qrLabel}
          value={selectedTopUpAddress}
          iconUrl={selectedCurrencyIconUrl}
        />
        <span className="new-card__qr-caption">
          {t('deposit.depositAsset', { asset: assetNetworkLabel })}
        </span>
      </div>

      <button
        type="button"
        className="new-card__copy-card"
        onClick={() => handleCopyValue(amount, common.copiedLabels.amount)}
      >
        <span className="new-card__copy-label">
          {t('deposit.amountWithAsset', { asset: assetNetworkLabel })}
        </span>
        <span className="new-card__copy-row">
          <span className="new-card__copy-value">{expectedAmount}</span>
          <CopyIcon className="new-card__copy-icon" />
        </span>
      </button>

      <button
        type="button"
        className="new-card__copy-card"
        onClick={() => handleCopyValue(selectedTopUpAddress, common.copiedLabels.wallet)}
      >
        <span className="new-card__copy-label">
          {t('deposit.walletAddress', { asset: assetNetworkLabel })}
        </span>
        <span className="new-card__copy-row">
          <span className="new-card__copy-value new-card__copy-value--address">
            {selectedTopUpAddress}
          </span>
          <CopyIcon className="new-card__copy-icon" />
        </span>
      </button>

      <div className="new-card__progress-card">
        <div className="new-card__progress-header">
          <span>
            {deposit.received}
            {selectedAssetCode ? ` ${selectedAssetCode}` : ''}
          </span>
          <span>
            {receivedTopUpLabel} / {expectedTopUpLabel}
          </span>
        </div>
        <div className="new-card__progress-track" aria-hidden="true">
          <motion.span
            className="new-card__progress-value"
            initial={false}
            animate={{ transform: `scaleX(${topUpProgressPercent / 100})` }}
            transition={shouldReduceMotion
              ? { duration: 0 }
              : { duration: 0.32, ease: 'easeOut' }}
          />
        </div>
        <p className="new-card__progress-text">
          {deposit.autoIssueText}
        </p>
      </div>

      <DepositOperations
        operations={depositOperations}
        showCardProgress={false}
        confirmationsText={deposit.issueTransferDetected}
      />

      <div className="new-card__warning">
        {minimumTransferWarning}
      </div>
    </div>
  );

  return (
    <CardPage className="new-card">
      <div className="new-card__content">
        <ScreenHeader title={headerTitle} onBack={headerBack} />

        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={step}
            initial={shouldReduceMotion
              ? { opacity: 0, transform: 'translate3d(0, 0px, 0)' }
              : { opacity: 0, transform: 'translate3d(0, 12px, 0)' }}
            animate={{ opacity: 1, transform: 'translate3d(0, 0px, 0)' }}
            exit={shouldReduceMotion
              ? { opacity: 0, transform: 'translate3d(0, 0px, 0)' }
              : { opacity: 0, transform: 'translate3d(0, -8px, 0)' }}
            transition={SCREEN_TRANSITION}
          >
            {step === STEP.SETUP && renderSetup()}
            {step === STEP.AMOUNT && renderAmount()}
            {step === STEP.ADDRESS && renderAddress()}
          </motion.div>
        </AnimatePresence>
      </div>

      {shouldShowCta && (
        <BottomActionButton
          className="new-card__cta"
          disabled={ctaDisabled}
          aria-describedby={step === STEP.AMOUNT && CARD_ISSUANCE_MAINTENANCE
            ? 'card-issuance-maintenance'
            : undefined}
          onClick={handleCta}
        >
          {ctaLabel}
        </BottomActionButton>
      )}

      <AnimatePresence onExitComplete={handleAssetSheetExitComplete}>
        {isAssetSheetOpen && (
          <FundingAssetSheet
            assets={fundingAssets}
            selectedAsset={selectedFundingAsset}
            onSelect={handleFundingAssetSelect}
            onClose={handleAssetSheetClose}
          />
        )}
      </AnimatePresence>
    </CardPage>
  );
}

export default NewCard;
